'use client';

/**
 * Zone d'affichage des résultats de recherche
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'orchestration de la zone résultats
 * - Délègue le rendu aux sous-composants
 *
 * @module EntitySearchResults
 */

import type { BaseHit } from 'instantsearch.js';
import { useStats } from 'react-instantsearch';

import { useEntitySearchConfig } from '../context/EntitySearchConfigContext';
import { EntityHits } from '../widgets/EntityHits';

// ============================================================================
// Types
// ============================================================================

export interface EntitySearchResultsProps<T extends BaseHit> {
  /**
   * Fonction render prop pour afficher une carte
   */
  renderHitCard: (hit: T) => React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Zone d'affichage des résultats
 *
 * Contient:
 * - État NoResults (si aucun résultat)
 * - Résultats (EntityHits avec render prop)
 * - Pagination
 *
 * @example
 * ```tsx
 * <EntitySearchResults
 *   renderHitCard={(hit) => <AuteurCard auteur={hit} />}
 * />
 * ```
 */
export function EntitySearchResults<T extends BaseHit>({
  renderHitCard,
}: EntitySearchResultsProps<T>) {
  const { nbHits } = useStats();
  const { customNoResultsMessages } = useEntitySearchConfig();

  // NoResults state
  if (nbHits === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
        <h3 className="mt-4 text-lg font-semibold text-gray-900">
          {customNoResultsMessages?.titre || 'Aucun résultat'}
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          {customNoResultsMessages?.message || 'Aucun résultat trouvé'}
        </p>
      </div>
    );
  }

  // Résultats + Pagination
  return (
    <div className="">
      {/* TODO: ActiveRefinements */}

      {/* Résultats */}
      <EntityHits renderHitCard={renderHitCard} />
    </div>
  );
}
